import {
  SmartToy as BotIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../firebase';
import { clientApi } from '../services/clientApi';
import { dietitianApi } from '../services/dietitianApi';
import { mealApi } from '../services/mealApi';
import { taroBotApi, TaroBotResponse } from '../services/taroBotApi';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

interface TaroBotProps {
  clientId?: string;
  dietitianId?: string;
  isDietitian?: boolean;
}

const TaroBot: React.FC<TaroBotProps> = ({
  clientId,
  dietitianId,
  isDietitian = false,
}) => {
  const [open, setOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [calorieInfo, setCalorieInfo] = useState<{
    target: number;
    consumed: number;
    remaining: number;
  } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);

  // Mesajları localStorage'a kaydet
  const saveMessages = useCallback((id: string, msgs: Message[]) => {
    try {
      localStorage.setItem(`taro-messages-${id}`, JSON.stringify(msgs));
    } catch (error) {
      console.error('Mesajlar kaydedilemedi:', error);
    }
  }, []);

  // Mesajları localStorage'dan yükle
  const loadMessages = useCallback(
    (id: string) => {
      try {
        const savedMessages = localStorage.getItem(`taro-messages-${id}`);
        if (savedMessages) {
          const parsed = JSON.parse(savedMessages);
          // Timestamp'leri Date'e çevir
          const messagesWithDates = parsed.map((msg: Message) => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }));
          setMessages(messagesWithDates);
        } else {
          // İlk açılışta hoş geldin mesajı
          const welcomeMessage: Message = {
            id: 'welcome',
            text: isDietitian
              ? 'Merhaba! Ben Taro, beslenme danışmanlığı asistanınız. Size nasıl yardımcı olabilirim? 😊'
              : 'Merhaba! Ben Taro, beslenme danışmanınız. Size nasıl yardımcı olabilirim? 😊',
            isBot: true,
            timestamp: new Date(),
          };
          setMessages([welcomeMessage]);
          saveMessages(id, [welcomeMessage]);
        }
      } catch (error) {
        console.error('Mesajlar yüklenemedi:', error);
        // Hata durumunda hoş geldin mesajı göster
        const welcomeMessage: Message = {
          id: 'welcome',
          text: isDietitian
            ? 'Merhaba! Ben Taro, beslenme danışmanlığı asistanınız. Size nasıl yardımcı olabilirim? 😊'
            : 'Merhaba! Ben Taro, beslenme danışmanınız. Size nasıl yardımcı olabilirim? 😊',
          isBot: true,
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
      }
    },
    [saveMessages, isDietitian]
  );

  useEffect(() => {
    if (isDietitian) {
      // Diyetisyen için
      if (dietitianId) {
        setCurrentClientId(dietitianId);
        loadMessages(dietitianId);
      } else {
        // Prop yoksa Firebase auth'tan al
        const user = auth.currentUser;
        if (user?.email) {
          dietitianApi
            .getDietitianByEmail(user.email)
            .then((dietitian) => {
              setCurrentClientId(dietitian.id);
              loadMessages(dietitian.id);
            })
            .catch((err) => {
              console.error('Diyetisyen bulunamadı:', err);
            });
        }
      }
    } else {
      // Client için
      if (clientId) {
        setCurrentClientId(clientId);
        loadCalorieInfo(clientId);
        loadMessages(clientId);
      } else {
        // Prop yoksa Firebase auth'tan al
        const user = auth.currentUser;
        if (user?.email) {
          clientApi
            .getClientByEmail(user.email)
            .then((client) => {
              setCurrentClientId(client.id);
              loadCalorieInfo(client.id);
              loadMessages(client.id);
            })
            .catch((err) => {
              console.error('Client bulunamadı:', err);
            });
        }
      }
    }
  }, [clientId, dietitianId, isDietitian, loadMessages]);

  useEffect(() => {
    if (open && currentClientId && messages.length === 0) {
      loadMessages(currentClientId);
    }
  }, [open, currentClientId, messages.length, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadCalorieInfo = async (id: string) => {
    try {
      const client = await clientApi.getClientById(id);
      const targetCalories = client.dailyCalories || 2000;

      const today = new Date();
      const dailySummary = await mealApi.getDailyNutritionSummary(id, today);
      const consumedCalories = dailySummary.totalCalories;

      setCalorieInfo({
        target: targetCalories,
        consumed: consumedCalories,
        remaining: targetCalories - consumedCalories,
      });
    } catch (error) {
      console.error('Kalori bilgisi yüklenemedi:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !currentClientId || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputMessage.trim(),
      isBot: false,
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    saveMessages(currentClientId, updatedMessages);
    const messageToSend = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    try {
      // Conversation history'yi hazırla (hoş geldin mesajını hariç tut)
      const conversationHistory = messages
        .filter((msg) => msg.id !== 'welcome')
        .map((msg) => ({
          role: msg.isBot ? ('assistant' as const) : ('user' as const),
          content: msg.text,
        }));

      let response: TaroBotResponse;

      if (isDietitian) {
        // Diyetisyen için
        response = await taroBotApi.chatDietitian({
          dietitianId: currentClientId,
          message: messageToSend,
          conversationHistory,
        });
      } else {
        // Client için
        response = await taroBotApi.chat({
          clientId: currentClientId,
          message: messageToSend,
          conversationHistory,
        });

        // Kalori bilgilerini güncelle (sadece client için)
        setCalorieInfo({
          target: response.targetCalories,
          consumed: response.consumedCalories,
          remaining: response.remainingCalories,
        });
      }

      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.message,
        isBot: true,
        timestamp: new Date(),
      };

      const finalMessages = [...updatedMessages, botMessage];
      setMessages(finalMessages);
      saveMessages(currentClientId, finalMessages);
    } catch (error: any) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.',
        isBot: true,
        timestamp: new Date(),
      };
      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);
      saveMessages(currentClientId, finalMessages);
      console.error('Taro bot hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmClose = (clearHistory: boolean) => {
    setShowConfirmDialog(false);
    if (clearHistory && currentClientId) {
      // localStorage'ı temizle
      localStorage.removeItem(`taro-messages-${currentClientId}`);
      // Mesajları sıfırla
      setMessages([]);
    }
    setOpen(false);
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="Taro Bot"
        onClick={() => setOpen(true)}
        disabled={!currentClientId}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 64,
          height: 64,
          bgcolor: 'rgba(67, 160, 71, 0.9)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 20px rgba(67, 160, 71, 0.4)',
          zIndex: 1000,
          animation: 'pulse 2s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              transform: 'scale(1)',
              boxShadow: '0 4px 20px rgba(67, 160, 71, 0.4)',
            },
            '50%': {
              transform: 'scale(1.05)',
              boxShadow: '0 6px 30px rgba(67, 160, 71, 0.6)',
            },
          },
          '&:hover': {
            animation: 'none',
            bgcolor: 'rgba(56, 142, 60, 0.95)',
            transform: 'scale(1.1)',
            boxShadow: '0 8px 35px rgba(67, 160, 71, 0.7)',
          },
          '&:disabled': {
            bgcolor: 'rgba(67, 160, 71, 0.5)',
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        }}
      >
        <BotIcon sx={{ fontSize: 32, color: 'white' }} />
      </Fab>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Sohbeti Sonlandır</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sohbeti sonlandırmak istiyor musunuz? Mesaj geçmişi silinecektir.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleConfirmClose(false)} color="inherit" sx={{ '&:hover': { bgcolor: 'rgba(170, 170, 170, 0.08)' } }}>
            Hayır
          </Button>
          <Button
            onClick={() => handleConfirmClose(true)}
            color="primary"
            variant="contained"
            sx={{ bgcolor: '#43A047', '&:hover': { bgcolor: '#388E3C' } }}
          >
            Evet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            inputRef.current?.focus();
          },
        }}
        PaperProps={{
          sx: {
            height: '80vh',
            maxHeight: 700,
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: '#43A047',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'white',
                color: '#43A047',
                width: 40,
                height: 40,
              }}
            >
              <BotIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Taro
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {isDietitian
                  ? 'Beslenme Danışmanlığı Asistanınız'
                  : 'Beslenme Danışmanınız'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            sx={{ color: 'white', width: 20, height: 20 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Kalori Bilgisi */}
        {calorieInfo && (
          <Box
            sx={{
              p: 2,
              bgcolor: '#F9FAF7',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Hedef: ${calorieInfo.target.toFixed(0)} kcal`}
                size="small"
                sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }}
              />
              <Chip
                label={`Alınan: ${calorieInfo.consumed.toFixed(0)} kcal`}
                size="small"
                sx={{ bgcolor: '#FFF3E0', color: '#E65100' }}
              />
              <Chip
                label={`Kalan: ${calorieInfo.remaining.toFixed(0)} kcal`}
                size="small"
                sx={{
                  bgcolor: calorieInfo.remaining > 0 ? '#E3F2FD' : '#FFEBEE',
                  color: calorieInfo.remaining > 0 ? '#1565C0' : '#C62828',
                }}
              />
            </Box>
          </Box>
        )}

        {/* Messages */}
        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            bgcolor: '#F9FAF7',
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.isBot ? 'flex-start' : 'flex-end',
                mb: 1,
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  maxWidth: '75%',
                  p: 1.5,
                  bgcolor: message.isBot ? 'white' : '#43A047',
                  color: message.isBot ? '#263238' : 'white',
                  borderRadius: message.isBot
                    ? '20px 20px 20px 4px'
                    : '20px 20px 4px 20px',
                }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {message.text}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.5,
                    opacity: 0.7,
                    fontSize: '0.7rem',
                  }}
                >
                  {formatTime(message.timestamp)}
                </Typography>
              </Paper>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: '20px 20px 20px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '@keyframes typing': {
                    '0%, 60%, 100%': {
                      transform: 'translateY(0)',
                      opacity: 0.7,
                    },
                    '30%': {
                      transform: 'translateY(-8px)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#607D8B',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  Taro yazıyor
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#43A047',
                      animation: 'typing 1.4s infinite',
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#43A047',
                      animation: 'typing 1.4s infinite 0.2s',
                    }}
                  />
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#43A047',
                      animation: 'typing 1.4s infinite 0.4s',
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </DialogContent>

        {/* Input */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e2e8f0',
            bgcolor: 'white',
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={3}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Taro'ya sorun..."
            variant="outlined"
            size="small"
            disabled={loading || !currentClientId}
            inputRef={inputRef}
            autoFocus={open}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                bgcolor: '#F9FAF7',
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              handleSendMessage();
              inputRef.current?.focus();
            }}
            disabled={!inputMessage.trim() || loading || !currentClientId}
            sx={{
              bgcolor: '#43A047',
              color: 'white',
              width: 48,
              height: 48,
              '&:hover': {
                bgcolor: '#388E3C',
              },
              '&:disabled': {
                bgcolor: '#E0E0E0',
                color: '#9E9E9E',
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Dialog>
    </>
  );
};

export default TaroBot;
